import {
  AccountType,
  IAccountResponse,
  ITraderResponse,
  IDepositorResponse,
  IStakeResponse,
  ICollateralRequest,
  IWithdrawRequest,
  IRemoveStakeRequest,
  IPoolBalanceHistory,
  IAutoStakeRequest,
  TPoolHistoryType,
  ISubnetChartDataResponse,
  ISubnetResponse,
  ISubnetDetailResponse,
  IWaitlistResponse,
  IAutoStakeResponse
} from './types';
import { SN_VAL_ADDRS } from './const';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// API Services
export const apiServices = {
  // Account APIs
  accounts: {
    getAll: async (skip = 0, limit = 100): Promise<IAccountResponse[]> => {
      const response = await fetch(`${API_BASE_URL}/accounts/?skip=${skip}&limit=${limit}`);
      return response.json();
    },

    create: async (address: string, type: AccountType): Promise<IAccountResponse> => {
      const response = await fetch(`${API_BASE_URL}/accounts/?address=${address}&type=${type}`, {
        method: 'POST',
      });
      return response.json();
    },

    getByAddress: async (address: string): Promise<IAccountResponse> => {
      const response = await fetch(`${API_BASE_URL}/accounts/${address}`);
      return response.json();
    },
  },

  // Trader APIs
  traders: {
    getAll: async (skip = 0, limit = 100): Promise<ITraderResponse[]> => {
      const response = await fetch(`${API_BASE_URL}/traders/?skip=${skip}&limit=${limit}`);
      return response.json();
    },

    getById: async (accountId: string): Promise<ITraderResponse> => {
      const response = await fetch(`${API_BASE_URL}/traders/${accountId}`);
      return response.json();
    },

    update: async (
      accountId: string,
      data: Partial<{
        collateral_amount: number | string;
        available_amount: number | string;
        staked_amount: number | string;
        current_stakes_amount: number | string;
      }>
    ): Promise<ITraderResponse> => {
      const params = new URLSearchParams();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined) {
          params.append(key, value.toString());
        }
      });

      const response = await fetch(`${API_BASE_URL}/traders/${accountId}?${params.toString()}`, {
        method: 'PUT',
      });
      return response.json();
    },

    addCollateral: async (data: ICollateralRequest, signature: string): Promise<ITraderResponse> => {
      const email = localStorage.getItem('waitlist_email') || "";
      const response = await fetch(`${API_BASE_URL}/traders/add-collateral`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Signature': signature,
          'X-Email': email,
          'X-Address': data.trader_id
        },
        body: JSON.stringify(data),
      });
      const responseData = await response.json();
      return {
        ...responseData,
        error_code: response.status || 0
      };
    },

    withdrawCollateral: async (data: IWithdrawRequest, signature: string): Promise<ITraderResponse> => {
      const email = localStorage.getItem('waitlist_email') || "";
      const response = await fetch(`${API_BASE_URL}/traders/withdraw-collateral`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Signature': signature,
          'X-Email': email,
          'X-Address': data.trader_id
        },
        body: JSON.stringify(data),
      });
      const responseData = await response.json();
      return {
        ...responseData,
        error_code: response.status || 0
      };
    },
  },

  // Depositor APIs
  depositors: {
    getAll: async (skip = 0, limit = 100): Promise<IDepositorResponse[]> => {
      const response = await fetch(`${API_BASE_URL}/depositors/?skip=${skip}&limit=${limit}`);
      return response.json();
    },

    getById: async (accountId: string): Promise<IDepositorResponse> => {
      const response = await fetch(`${API_BASE_URL}/depositors/${accountId}`);
      return response.json();
    },

    update: async (
      accountId: number,
      data: Partial<{
        deposit_amount: number | string;
        deposit_percent: number | string;
      }>
    ): Promise<IDepositorResponse> => {
      const params = new URLSearchParams();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined) {
          params.append(key, value.toString());
        }
      });

      const response = await fetch(`${API_BASE_URL}/depositors/${accountId}?${params.toString()}`, {
        method: 'PUT',
      });
      return response.json();
    },

    deposit: async (depositorId: string, amount: number | string, signature: string): Promise<IDepositorResponse> => {
      const email = localStorage.getItem('waitlist_email') || "";
      const response = await fetch(
        `${API_BASE_URL}/depositors/${depositorId}/deposit?amount=${amount}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Signature': signature,
            'X-Email': email,
            'X-Address': depositorId
          },
        }
      );
      const data = await response.json();
      return {
        ...data,
        error_code: response.status || 0
      };
    },

    withdraw: async (depositorId: string, amount: number | string, signature: string): Promise<IDepositorResponse> => {
      const email = localStorage.getItem('waitlist_email') || "";
      const response = await fetch(
        `${API_BASE_URL}/depositors/${depositorId}/withdraw?amount=${amount}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Signature': signature,
            'X-Email': email,
            'X-Address': depositorId
          },
        }
      );
      const data = await response.json();
      return {
        ...data,
        error_code: response.status || 0
      };
    },
  },

  // Stake APIs
  stakes: {
    add: async (
      traderId: string,
      subnet: number,
      staked: number | string,
      signature: string
    ): Promise<IStakeResponse> => {
      const email = localStorage.getItem('waitlist_email') || "";
      const response = await fetch(
        `${API_BASE_URL}/stakes/add?trader_id=${traderId}&subnet=${subnet}&validator=${SN_VAL_ADDRS[subnet]}&staked=${staked}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Signature': signature,
            'X-Email': email,
            'X-Address': traderId
          }
        }
      );
      const data = await response.json();
      return {
        ...data,
        error_code: response.status || 0
      };
    },

    remove: async (data: IRemoveStakeRequest, signature: string): Promise<IStakeResponse> => {
      const email = localStorage.getItem('waitlist_email') || "";
      const response = await fetch(`${API_BASE_URL}/stakes/remove`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Signature': signature,
          'X-Email': email,
          'X-Address': data.trader_id
        },
        body: JSON.stringify(data),
      });
      const stakeData = await response.json();
      return {
        ...stakeData,
        error_code: response.status || 0
      };
    },

    autoStake: async (data: IAutoStakeRequest, signature: string): Promise<IStakeResponse> => {
      const email = localStorage.getItem('waitlist_email') || "";
      const response = await fetch(`${API_BASE_URL}/stakes/auto-stake`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Signature': signature,
          'X-Email': email,
          'X-Address': data.trader_id
        },
        body: JSON.stringify(data),
      });
      const stakeData = await response.json();
      return {
        ...stakeData,
        error_code: response.status || 0
      };
    },

    stopAutoStake: async (autoStakeId: number, traderAddress: string, signature: string): Promise<any> => {
      const email = localStorage.getItem('waitlist_email') || "";
      const response = await fetch(`${API_BASE_URL}/stakes/stop-auto-stake`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Signature': signature,
          'X-Email': email,
          'X-Address': traderAddress
        },
        body: JSON.stringify({ auto_stake_id: autoStakeId }),
      });
      const stakeData = await response.json();
      return {
        ...stakeData,
        error_code: response.status || 0
      };
    },

    getAutoStakes: async ( trader_addr: string ): Promise<IAutoStakeResponse[]> => {
      const response = await fetch(`${API_BASE_URL}/stakes/auto-stake/${trader_addr}`);
      return response.json();
    },

    getSubnetPrice: async (subnetId: number): Promise<number> => {
      const response = await fetch(`${API_BASE_URL}/stakes/alpha-to-tao?netuid=${subnetId}&amount=1`);
      const data = await response.json();
      return data.tao_amount || 0;
    }
  },

  // Pool APIs
  pool: {
    getBalances: async (type: TPoolHistoryType = "day"): Promise<Record<string, string | IPoolBalanceHistory[]>> => {
      const response = await fetch(`${API_BASE_URL}/pool/balances?type=${type}`);
      return response.json();
    },
    refresh: async (address: string, is_trader: boolean): Promise<ITraderResponse | IDepositorResponse> => {
      const response = await fetch(`${API_BASE_URL}/pool/refresh?address=${address}&is_trader=${is_trader}`);
      return response.json();
    },
  },
  // Subnet APIs
  price: {
    subnet: async (subnet: string): Promise<ISubnetDetailResponse> => {
      const response = await fetch(`${API_BASE_URL}/pool/subnets/${subnet}`);
      return response.json();
    },
    subnets: async (): Promise<ISubnetResponse[]> => {
      const response = await fetch(`${API_BASE_URL}/pool/subnets`);
      return response.json();
    },
    chart: async (subnet: string, interval: string, start_time: number, end_time: number): Promise<ISubnetChartDataResponse[]> => {
      const response = await fetch(`${API_BASE_URL}/pool/chart?netuid=${subnet}&interval=${interval}&start_time=${start_time}&end_time=${end_time}`);
      return response.json();
    },
  },
  waitlist: {
    join: async (email: string): Promise<IWaitlistResponse> => {
      const response = await fetch(`${API_BASE_URL}/participants/add-waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      return response.json();
    },
  },
}; 